import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './TopUpPage.css';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import TopBar from './TopBar';
import { getManualTopUpsForUser, MANUAL_TOPUP_COLLECTION_ID } from '../services/manualTopUpService';
import { client } from '../lib/appwrite';

const DATABASE_ID = process.env.REACT_APP_APPWRITE_DATABASE_ID;

const PACKAGES = [
  { id: 'p15', label: 'RM 15', amount: 15 },
  { id: 'p25', label: 'RM 25', amount: 25 },
  { id: 'p50', label: 'RM 50', amount: 50 }
];

export default function TopUpPage() {
  const { user, profile, refreshProfile } = useAuth();
  const { showSuccess, showError } = useToast();
  const [selected, setSelected] = useState(PACKAGES[0].id);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // Memoize history fetch to avoid recreation and potential loops
  const fetchHistory = useCallback(async (userId) => {
    try {
      const manualHistory = await getManualTopUpsForUser(userId);
      setHistory(manualHistory);
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // Initialization and Realtime Subscription
  useEffect(() => {
    if (user?.$id) {
      // Only show loader if we don't have history yet to prevent jittering
      if (history.length === 0) {
        setHistoryLoading(true);
      }

      // Initial load of profile and history
      if (refreshProfile) refreshProfile();
      fetchHistory(user.$id);

      // Realtime subscription for top-up status updates
      const subscriptionPath = `databases.${DATABASE_ID}.collections.${MANUAL_TOPUP_COLLECTION_ID}.documents`;

      const unsubscribe = client.subscribe(subscriptionPath, (response) => {
        // If the updated document belongs to this user
        if (response.payload.user_id === user.$id) {
          console.log('Realtime update detected for top-up:', response.events);

          // Refresh data without showing global loader for seamless updates
          fetchHistory(user.$id);
          if (refreshProfile) refreshProfile();
        }
      });

      return () => unsubscribe();
    }
    // Note: fetchHistory is stable (useCallback), refreshProfile is stable now too.
  }, [user?.$id, fetchHistory, refreshProfile]);

  const handleTopUp = async () => {
    if (!user) { showError('Please login to top up'); return; }
    const pkg = PACKAGES.find(p => p.id === selected);
    setLoading(true);
    try {
      // Prefer explicit server URL via REACT_APP_SERVER_URL in development, otherwise use same-origin
      const baseUrl = process.env.REACT_APP_SERVER_URL || '';
      const endpoint = baseUrl + '/api/topup/create-checkout-session';

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: pkg.amount, user_id: user.$id })
      });

      // If HTML is returned (e.g., 404 served by dev server), surface that for debugging
      const contentType = res.headers.get('content-type') || '';
      if (!res.ok) {
        const text = await res.text();
        console.error('Top up request failed:', res.status, text);
        showError('Failed to create payment session');
        return;
      }

      if (contentType.includes('application/json')) {
        const data = await res.json();
        if (data.url) {
          window.location = data.url; // redirect to Stripe Checkout
        } else {
          showError('Failed to create payment session');
          console.error(data);
        }
      } else {
        const text = await res.text();
        console.error('Unexpected non-JSON response:', text);
        showError('Unexpected response from server. Please try again.');
      }
    } catch (error) {
      console.error('Top up error', error);
      showError('Failed to initiate top up. Try again later.');
    } finally { setLoading(false); }
  };

  return (
    <div className="topup-page">
      <TopBar />
      <div className="topup-container">
        <h1>Top up your balance</h1>
        {profile && profile.wallet_balance !== undefined && (
          <div className="current-balance">
            <h3>Current Balance: RM {Number(profile.wallet_balance).toFixed(2)}</h3>
          </div>
        )}
        <div className="topup-content">
          <div className="automated-notice">
            <p className="notice-main">Automated top-up is coming very soon!</p>
            <p className="notice-sub">While we wait for approval, you can top-up your wallet manually by uploading a bank-in receipt.</p>
            <Link to="/topup/manual" className="manual-topup-link-btn">
              Top Up Manually ➔
            </Link>
          </div>

          <div className="topup-packages">
            <h2>Choose a package <span style={{ fontWeight: 'normal', fontSize: '0.9em', color: '#666' }}>(For Automated Top-up Only)</span></h2>
            {PACKAGES.map(p => (
              <label key={p.id} className={`package ${selected === p.id ? 'selected' : ''} disabled-pkg`}>
                <input type="radio" name="pkg" checked={selected === p.id} onChange={() => setSelected(p.id)} disabled />
                <div className="package-amount">RM {p.amount.toFixed(2)}</div>
              </label>
            ))}
            <button className="topup-btn disabled-btn" disabled>Direct Payment Currently Unavailable</button>
          </div>
        </div>

        <div className="manual-history-section">
          <h2>Top-Up History</h2>
          {historyLoading ? (
            <div className="history-loader">Loading history...</div>
          ) : history.length > 0 ? (
            <div className="history-list">
              {history.map((req) => (
                <div key={req.$id} className={`history-card ${req.status}`}>
                  <div className="history-main">
                    <div className="history-amount">RM {parseFloat(req.amount).toFixed(2)}</div>
                    <div className="history-date">{new Date(req.$createdAt).toLocaleDateString()}</div>
                  </div>
                  <div className="history-status">
                    <span className={`status-badge ${req.status}`}>
                      {req.status === 'completed' ? '✅ Completed' :
                        req.status === 'rejected' ? '❌ Rejected' : '⏳ Pending'}
                    </span>

                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-history">No manual top-up requests found.</p>
          )}
        </div>
      </div>
    </div>
  );
}